import { FC } from "react"

import { useTranslation } from "react-i18next"

import { PLAYER_LOCALES } from "constants/player-locales"

import { CmsPage } from "services/cms-seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { LocaleChipBar } from "../CmsUi"

interface LocaleBarProps {
  page: CmsPage
  /** True while a translation is being created, so the bar cannot be double-fired. */
  busy: boolean
  onSwitch: (pageId: string) => void
  onCreate: (locale: string) => void
}

/** Every language is one click: an existing one switches, a missing one is created. */
export const LocaleBar: FC<LocaleBarProps> = ({ page, busy, onSwitch, onCreate }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const present = PLAYER_LOCALES.filter((locale) => page.translations[locale])

  // The chips lead to two different URL shapes on this brand, which is not
  // something an editor can infer from the page in front of them.
  const hint =
    site.localePrefix === "as-needed"
      ? t("cmsSeo.localeBarHintAsNeeded", {
          defaultValue:
            "A language with no version of its own is offered with a +. {{language}} is served from the root of the domain, so its pages carry no language code in the URL — every other language keeps its own.",
          language: site.defaultLocale.toUpperCase()
        })
      : t("cmsSeo.localeBarHint", {
          defaultValue: "A language with no version of its own is offered with a +"
        })

  return (
    <div className="border-b border-black/[0.07] px-4 py-2.5 sm:px-5 dark:border-white/[0.08]">
      <LocaleChipBar
        current={page.locale}
        present={present}
        busy={busy}
        onSelect={(locale) => {
          const sibling = page.translations[locale]
          if (sibling && locale !== page.locale) onSwitch(sibling.id)
        }}
        onCreate={onCreate}
        hint={hint as string}
      />
    </div>
  )
}
